import { z } from 'zod';
import { placeIdSchema } from './index.js';
/**
 * The coverage map's one server route (ADR-018 Q8 "MP safe", #474 part B; our own pins over the picture, #563).
 *
 * WHAT CROSSES THE WIRE. The browser sends place ids. It gets back the address of a picture, the picture's size, and
 * where in the picture each place sits. The picture's address holds the map's centre and zoom, the map key, the size and
 * the signature, and nothing about a person: this server never has a person in hand when it builds one. Since #563 it
 * holds no marker either: the pins are the page's own buttons, drawn over the picture, so they can be clicked.
 *
 * NOTHING IS KEPT. The positions are read from Google for this one answer and held nowhere
 * (ADR-018 §1: coordinates may not be kept longer than Google allows, so none are kept at all).
 */
/**
 * The picture, in CSS pixels at scale 1: wider than tall, to suit Virginia (#563, frame A1). Google's Maps Static API
 * allows up to 640 x 640 at scale 1, and both are inside it.
 */
export const COVERAGE_MAP_WIDTH = 400;
export const COVERAGE_MAP_HEIGHT = 272;
/**
 * A pin carries ONE letter, A to Z, so at most 26 places get one (#474, decision MP4). The letters are given in the
 * page, A to Z by the place's name (decision `ML alpha`): the first place in that order is A.
 */
export const COVERAGE_MAP_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
export const COVERAGE_MAP_MAX_PINS = COVERAGE_MAP_LETTERS.length;
/** The most place ids one request may name. Only the first `COVERAGE_MAP_MAX_PINS` are ever read from Google. */
export const COVERAGE_MAP_IDS_MAX = 200;
/** `?ids=a,b,c`: distinct, in the order given (the first 26 are the ones read), at most `COVERAGE_MAP_IDS_MAX`. */
export const coverageMapQuerySchema = z
    .string()
    .max(COVERAGE_MAP_IDS_MAX * 301)
    .transform((raw) => raw.split(',').map((part) => part.trim()).filter((part) => part !== ''))
    .pipe(z.array(placeIdSchema).min(1).max(COVERAGE_MAP_IDS_MAX))
    .transform((ids) => [...new Set(ids)]);
/**
 * A pin: which place, and where its middle sits in the picture, as a share of the picture's width (`x`, from the left)
 * and height (`y`, from the top). A share, not pixels, so the pin stays on its place when the picture is drawn smaller.
 */
export const coveragePinSchema = z.object({
    placeId: placeIdSchema,
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
}).strict();
/**
 * The answer: ONE picture, whatever is chosen (the ring on a chosen pin is drawn by the page, so choosing a place
 * loads nothing). Places with no pin are listed in `unpinned`: more than 26, or Google gave no position for them.
 */
export const coverageMapResponseSchema = z.object({
    pictureUrl: z.string().url().max(8_000),
    width: z.literal(COVERAGE_MAP_WIDTH),
    height: z.literal(COVERAGE_MAP_HEIGHT),
    pins: z.array(coveragePinSchema).min(1).max(COVERAGE_MAP_MAX_PINS),
    unpinned: z.array(placeIdSchema).max(COVERAGE_MAP_IDS_MAX),
}).strict();
export const COVERAGE_MAP_UNAVAILABLE_MESSAGE = 'The map cannot be shown right now. The list of places and the roster still work.';
/** Where Google serves the picture from: the one host `img-src` allows on the staff pages. */
export const COVERAGE_MAP_IMAGE_ORIGIN = 'https://maps.googleapis.com';
//# sourceMappingURL=coverage-map.js.map